import { useState } from 'react';
import { X } from 'lucide-react';
import { trackDonationAbandonFeedback } from '@/lib/utils/analytics';

type Reason = 'notReadyYet' | 'wantedMoreInfo' | 'askUnclear' | 'somethingBroken';

// Same four reasons the cc.concept.zone chat widget already asks site-wide
// (data-feedback-options on its <script> tag), kept word-for-word so this is
// one taxonomy, not a second one invented for the donation flow alone.
const OPTIONS: { value: Reason; label: string }[] = [
  { value: 'notReadyYet', label: 'Not ready to give yet' },
  { value: 'wantedMoreInfo', label: 'Wanted to know more about the athlete first' },
  { value: 'askUnclear', label: 'The ask felt unclear' },
  { value: 'somethingBroken', label: 'Something was broken' },
];

interface DonationAbandonFeedbackProps {
  creatorId: string;
  onDone: () => void;
}

/**
 * TSF-283: shown when a donor closes the donate dialog without ever starting
 * checkout (MissionContent gates this on checkoutStarted never having fired
 * for the session) -- the one spot in the funnel donate_ask_viewed ->
 * checkout_started -> purchase has never had qualitative signal on the drop.
 */
export default function DonationAbandonFeedback({ creatorId, onDone }: DonationAbandonFeedbackProps) {
  const [answered, setAnswered] = useState(false);

  const respond = (reason: Reason | 'dismissed') => {
    if (answered) return;
    trackDonationAbandonFeedback(reason, creatorId);
    setAnswered(true);
    window.setTimeout(onDone, 1600);
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/30 p-4">
      <div className="relative w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl shadow-black/10 animate-in fade-in zoom-in-95">
        <button
          type="button"
          onClick={() => respond('dismissed')}
          aria-label="Close"
          className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
        >
          <X className="h-4 w-4" />
        </button>
        {answered ? (
          <p className="pr-4 text-sm font-medium text-slate-700">Thanks — that helps us improve the ask.</p>
        ) : (
          <>
            <p className="pr-4 text-sm font-semibold text-slate-900">Quick one before you go — what would have made you give today?</p>
            <div className="mt-3 flex flex-col gap-2">
              {OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => respond(opt.value)}
                  className="rounded-full border border-slate-200 px-4 py-2 text-left text-sm font-medium text-slate-700 hover:border-blue-300 hover:bg-primary/10 hover:text-blue-800 transition-colors"
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
